import { Box, Checkbox, CircularProgress, useTheme } from "@mui/material";
import Flex from "../Flex";
import { OptionRow } from "./OptionRow";
import { MData } from "./types";
import { PRIMITIVE } from "../ds";
import { OPTIONS_LIST_MAX_HEIGHT } from "./mask-multi-select-constant";

interface OptionsListProps<T extends MData> {
  options: T[];
  labelKey: keyof T;
  idKey: keyof T;
  showSelectAll: boolean;
  allSelected: boolean;
  onToggleSelectAll: () => void;
  onToggleOption: (opt: T) => void;
  renderCustomLabel?: (opt: T) => React.ReactNode;
  onScroll?: (el: HTMLElement | null) => void;
  loading?: boolean;
}

export const OptionsList = <T extends MData>({
  options,
  labelKey,
  idKey,
  showSelectAll,
  allSelected,
  onToggleSelectAll,
  onToggleOption,
  renderCustomLabel,
  onScroll,
  loading,
}: OptionsListProps<T>) => {
  const theme = useTheme();

  return (
    <Box
      onScroll={(e) => onScroll?.(e.currentTarget)}
      sx={{ maxHeight: OPTIONS_LIST_MAX_HEIGHT, overflowY: "auto" }}
    >
      {showSelectAll && options.length > 0 && (
        <Flex
          alignItems="center"
          gap={1}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            onToggleSelectAll();
          }}
          sx={{
            px: 1.25,
            py: 0.75,
            cursor: "pointer",
            "&:hover": { backgroundColor: theme.palette.action.hover },
          }}
        >
          <Checkbox
            checked={allSelected}
            size="small"
            sx={{ p: 0 }}
            tabIndex={-1}
          />
          <Box
            sx={{
              fontSize: `${PRIMITIVE.typography.fontSize.body.b5}px`,
              fontWeight: 500,
            }}
          >
            All
          </Box>
        </Flex>
      )}

      {options.length === 0 ? (
        <Box
          sx={{
            py: 2,
            textAlign: "center",
            fontSize: `${PRIMITIVE.typography.fontSize.body.b5}px`,
            color: theme.palette.text.secondary,
          }}
        >
          No Result Found
        </Box>
      ) : (
        options.map((opt) => (
          <OptionRow
            key={String(opt[idKey])}
            option={opt}
            labelKey={labelKey}
            selected={false}
            onToggle={onToggleOption}
            renderCustomLabel={renderCustomLabel}
          />
        ))
      )}

      {loading && (
        <Flex alignItems="center" justifyContent="center" sx={{ py: 1 }}>
          <CircularProgress size={18} />
        </Flex>
      )}
    </Box>
  );
};
